import {ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {camelCaseToTitleCase} from '@aviary/store/transformation.helpers';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {getExtendedControlFrom, updateEnablementFor} from '@aviary/forms/form.helpers';
import {MatCheckbox} from '@angular/material/checkbox';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';

const DEFAULT_CHECKBOX_LABEL_CLASS: string = 'text-body-large';
const DEFAULT_CHECKBOX_HINT_CLASS: string = 'text-body-small';

@Component({
  selector: 'mx-checkbox-form-field',
  template: `
    <!-- Checkbox form fields are indented so they vertically align with the start of the text inside an input, as opposed to the left edge of the input. -->
    <mat-checkbox [formControl]="control" color="primary" class="ms-1.5">
      <span class="flex flex-col">
        <span class="{{ labelCssClasses }}">{{ overrideLabel ?? camelCaseToTitleCase(fieldName) }}</span>

        @if (hintLabel) {
          <span class="{{ hintLabelCssClasses }}">{{ hintLabel }}</span>
        }
      </span>
      <ng-content/>
    </mat-checkbox>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(CheckboxFormFieldComponent)],
  imports: [MatCheckbox, ReactiveFormsModule]
})
export class CheckboxFormFieldComponent implements OnInit, OnChanges, ControlRef {

  @Input() public overrideLabel?: string;
  @Input() public hintLabel?: string;
  @Input() public enabled: boolean = true;

  @Input()
  public set labelCssClasses(classes: string | null | undefined) {
    this._labelCssClasses = classes ?? DEFAULT_CHECKBOX_LABEL_CLASS;
  }

  public get labelCssClasses(): string {
    return this._labelCssClasses;
  }

  @Input()
  public set hintLabelCssClasses(classes: string | null | undefined) {
    this._hintLabelCssClasses = classes ?? DEFAULT_CHECKBOX_HINT_CLASS;
  }

  public get hintLabelCssClasses(): string {
    return this._hintLabelCssClasses;
  }

  @Input({required: true})
  public set fieldName(fieldName: string) {
    this._fieldName = fieldName;

    if (isDefined(this._formGroup)) {
      this.control = getExtendedControlFrom(this._formGroup, fieldName);
    }
  }

  public get fieldName(): string {
    return this._fieldName;
  }

  @Input({required: true})
  public set formGroup(formGroup: ExtendedFormGroup) {
    this._formGroup = formGroup;

    if (isDefined(this._fieldName)) {
      this.control = getExtendedControlFrom(formGroup, this._fieldName);
    }
  }

  public get formGroup(): ExtendedFormGroup {
    return this._formGroup;
  }

  public readonly camelCaseToTitleCase = camelCaseToTitleCase;
  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.

  private _fieldName: string;
  private _formGroup: ExtendedFormGroup;
  private _labelCssClasses = DEFAULT_CHECKBOX_LABEL_CLASS;
  private _hintLabelCssClasses = DEFAULT_CHECKBOX_HINT_CLASS;

  public ngOnChanges({control, enabled}: SimpleChanges): void {
    if (control || enabled) updateEnablementFor(this.control, this.enabled);
  }

  public ngOnInit(): void {
    if (!this.enabled) this.control.disable();
  }
}
